import { StreamingProtocol } from "../storage/data-model/source/storage-data-model-source"

export type Browser = 'chromium'|'safari'|'edge'|'internet-explorer'|'opera'|'opera-mini'|'firefox'|'node'
export type OS = 'windows'|'ios'|'android'|'mac'|'linux'|'chrome'|'other'
export type DRM = 'fairplay'|'widevine'|'playready'

/**
 * Device related functionality.
 * 
 * @author Stan Hurks
 */
export default class Device {
    /**
     * Detects the browser
     */
    public static browser: Browser = ((): Browser => {
        const _navigator = navigator || window?.navigator
        if (_navigator) {
            const platforms = [
                (_navigator as any)?.userAgentData?.platform,
                _navigator?.platform,
                _navigator.userAgent
            ]
            for (const platform of platforms) {
                if (/(Pike v[0-9]+\.[0-9]+ release [0-9]+)/.test(platform)) {
                    return 'opera-mini'
                }
            }

            return navigator.userAgent.match(/chrome|chromium|crios/i)
                ? 'chromium'
                : navigator.userAgent.match(/firefox|fxios/i)
                    ? 'firefox'
                    : navigator.userAgent.match(/safari/i)
                        ? 'safari'
                        : navigator.userAgent.match(/opr\//i)
                            ? 'opera'
                            : navigator.userAgent.match(/edg/i)
                                ? 'edge'
                                : window && window.document && (window.document as any).documentMode
                                    ? 'internet-explorer'
                                    : 'node'
        }
        return 'node'
    })()

    /**
     * Detects the operating system
     */
    public static os: OS = ((): OS => {
        const _navigator = navigator || window?.navigator
        if (_navigator) {
            const platforms = [
                (_navigator as any)?.userAgentData?.platform,
                _navigator?.platform,
                _navigator.userAgent
            ]
            for (const platform of platforms) { 
                if (!platform) {
                    continue
                }
                if (/Mac(intosh|Intel|PPC|68K)/.test(platform)) {
                    return 'mac'
                }
                else if (/(iP(hone|ad|od)( Simulator)?)|(Pike v[0-9]+\.[0-9]+ release [0-9]+)/.test(platform)) {
                    return 'ios'
                }
                else if (/Android/.test(platform)) {
                    return 'android'
                }
                else if (/Win(32|64|dows|CE)/.test(platform)) {
                    return 'windows'
                }
                else if (/Linux(aarch64|Linux armv5tejl|armv6l|armv7l|armv8l|i686|i686 on x86_64|i686 X11|MSM8960_v3\.2\.1\.1_N_R069_Rev\:18|ppc64|x86_64|x86_64 X11)?/.test(platform)) {
                    return 'linux'
                }
            }
        }
        return 'other'
    })()

    /**
     * Combinations for DRM
     */
    public static combinations: {
        [os in OS]:
            os extends 'other'
            ? null
            : {
                [browser in Browser]: browser extends Exclude<
                    'ios' extends Exclude<OS, os>
                    
                    // All but iOS
                    ? Exclude<
                        'mac' extends Exclude<OS, os>

                        // All but mac
                        ? Exclude<Browser, 'safari'>

                        // Mac
                        : Browser
                    , 'opera-mini'>

                    // iOS
                    : Browser,

                    // Exclude
                    'node'|'internet-explorer'
                >
                ? {
                    protocol: StreamingProtocol,
                    drm: DRM
                } | null // TODO: remove `| null`
                : null
            }
    } = {
        'android': {
            'chromium': {
                protocol: 'dash',
                drm: 'widevine'
            },
            'edge': {
                protocol: 'dash',
                drm: 'widevine'
            },
            // TODO: which combination of drm/protocol?
            'firefox': null,
            // TODO: which combination of drm/protocol?
            'opera': null,
            'internet-explorer': null,
            'opera-mini': null,
            'safari': null,
            'node': null,
        },
        'chrome': {
            // TODO: which combination of drm/protocol?
            'chromium': null,
            // TODO: which combination of drm/protocol?
            'edge': null,
            // TODO: which combination of drm/protocol?
            'firefox': null,
            // TODO: which combination of drm/protocol?
            'opera': null,
            'internet-explorer': null,
            'opera-mini': null,
            'safari': null,
            'node': null,
        },
        'ios': {
            // TODO: which combination of drm/protocol?
            'chromium': null,
            // TODO: which combination of drm/protocol?
            'edge': null,
            // TODO: which combination of drm/protocol?
            'firefox': null,
            // TODO: which combination of drm/protocol?
            'opera': null,
            'safari': {
                protocol: 'hls',
                drm: 'fairplay'
            },
            // TODO: which combination of drm/protocol?
            "opera-mini": null,
            'internet-explorer': null,
            'node': null,
        },
        'linux': {
            // TODO: which combination of drm/protocol?
            'chromium': null,
            // TODO: which combination of drm/protocol?
            'edge': null,
            // TODO: which combination of drm/protocol?
            'firefox': null,
            // TODO: which combination of drm/protocol?
            'opera': null,
            'internet-explorer': null,
            'opera-mini': null,
            'safari': null,
            'node': null,
        },
        'mac': {
            'chromium': {
                protocol: 'dash',
                drm: 'widevine'
            },
            'edge': {
                protocol: 'dash',
                drm: 'widevine'
            },
            'firefox': {
                protocol: 'dash',
                drm: 'widevine'  
            },
            // TODO: which combination of drm/protocol?
            'opera': null,
            'safari': {
                protocol: 'hls',
                drm: 'fairplay'
            },
            'internet-explorer': null,
            'opera-mini': null,
            'node': null,
        },
        'windows': {
            'chromium': {
                protocol: 'dash',
                drm: 'widevine'
            },
            'edge': {
                protocol: 'dash',
                drm: 'widevine'
            },
            'firefox': {
                protocol: 'dash',
                drm: 'widevine'  
            },
            // TODO: which combination of drm/protocol?
            'opera': null,
            'internet-explorer': null,
            'opera-mini': null,
            'safari': null,
            'node': null,
        },
        'other': null
    }

    /**
     * The chosen combination of DRM and streaming protocol
     */
    public static combination: {
        [os in OS]: typeof Device.combinations[os] extends null
            ? never
            : typeof Device.combinations[os][keyof typeof Device.combinations[os]]
    }[OS] = Device.combinations[Device.os] === null
        ? null
        : Device.combinations[Device.os]![Device.browser] || null
        
    /**
     * Detects the streaming protocol to use
     */
    public static protocol: StreamingProtocol|null = Device.combination?.protocol ?? null

    /**
     * Detects the DRM
     */
    public static drm: DRM|null = Device.combination?.drm ?? null
}